if (!customElements.get('mega-menu-tabs')) {
  customElements.define(
    'mega-menu-tabs',
    class MegaMenuTabs extends HTMLElement {
      connectedCallback() {
        this.primaryButtons = Array.from(this.querySelectorAll('.mega-menu__primary-button'));
        this.secondaryButtons = Array.from(this.querySelectorAll('.mega-menu__secondary-button'));
        this.secondaryPanels = Array.from(this.querySelectorAll('.mega-menu__secondary-panel'));
        this.productPanels = Array.from(this.querySelectorAll('.mega-menu__products-panel'));

        if (!this.primaryButtons.length) return;

        this.onPrimaryEnter = this.handlePrimaryEnter.bind(this);
        this.onSecondaryEnter = this.handleSecondaryEnter.bind(this);
        this.onResize = this.updateVisibleCarousel.bind(this);
        this.onParentToggle = this.handleParentToggle.bind(this);

        this.primaryButtons.forEach((button) => {
          button.addEventListener('mouseenter', this.onPrimaryEnter);
          button.addEventListener('focus', this.onPrimaryEnter);
        });
        this.secondaryButtons.forEach((button) => {
          button.addEventListener('mouseenter', this.onSecondaryEnter);
          button.addEventListener('focus', this.onSecondaryEnter);
        });
        window.addEventListener('resize', this.onResize);

        this.parentDetails = this.closest('details');
        this.parentDetails?.addEventListener('toggle', this.onParentToggle);

        this.carousels = this.productPanels
          .map((panel) => this.createCarousel(panel))
          .filter(Boolean);

        this.initializeState();
      }

      disconnectedCallback() {
        this.primaryButtons?.forEach((button) => {
          button.removeEventListener('mouseenter', this.onPrimaryEnter);
          button.removeEventListener('focus', this.onPrimaryEnter);
        });
        this.secondaryButtons?.forEach((button) => {
          button.removeEventListener('mouseenter', this.onSecondaryEnter);
          button.removeEventListener('focus', this.onSecondaryEnter);
        });
        this.parentDetails?.removeEventListener('toggle', this.onParentToggle);
        window.removeEventListener('resize', this.onResize);

        this.carousels?.forEach((carousel) => {
          carousel.prev?.removeEventListener('click', carousel.onPrev);
          carousel.next?.removeEventListener('click', carousel.onNext);
          carousel.scroller?.removeEventListener('scroll', carousel.onScroll);
        });
      }

      initializeState() {
        const initialPrimary = this.primaryButtons.find((button) => button.classList.contains('is-current')) || this.primaryButtons[0];
        this.activatePrimary(initialPrimary);
      }

      handleParentToggle() {
        if (!this.parentDetails?.open) return;
        requestAnimationFrame(() => {
          const currentPrimary = this.primaryButtons.find((button) => button.classList.contains('is-active')) || this.primaryButtons[0];
          if (currentPrimary) this.activatePrimary(currentPrimary);
          this.updateVisibleCarousel();
        });
      }

      handlePrimaryEnter(event) {
        this.activatePrimary(event.currentTarget);
      }

      handleSecondaryEnter(event) {
        this.activateProduct(event.currentTarget.dataset.megaProductTrigger, event.currentTarget);
      }

      activatePrimary(button) {
        if (!button) return;

        const targetPanelId = button.dataset.megaTrigger;
        const panel = this.secondaryPanels.find((item) => item.dataset.megaPanel === targetPanelId);

        this.primaryButtons.forEach((item) => item.classList.toggle('is-active', item === button));
        this.secondaryPanels.forEach((item) => item.classList.toggle('is-active', item === panel));

        const initialSecondary =
          panel?.querySelector('.mega-menu__secondary-button.is-current') || panel?.querySelector('.mega-menu__secondary-button');
        const fallbackProductPanel = button.dataset.defaultProductPanel;

        if (initialSecondary) {
          this.activateProduct(initialSecondary.dataset.megaProductTrigger, initialSecondary);
        } else if (fallbackProductPanel) {
          this.activateProduct(fallbackProductPanel);
        }
      }

      activateProduct(panelId, button = null) {
        if (!panelId) return;

        const activeSecondaryPanel = this.secondaryPanels.find((panel) => panel.classList.contains('is-active'));
        activeSecondaryPanel
          ?.querySelectorAll('.mega-menu__secondary-button')
          .forEach((item) => item.classList.toggle('is-active', item === button));

        this.productPanels.forEach((panel) => panel.classList.toggle('is-active', panel.dataset.megaProductPanel === panelId));

        const activePanel = this.productPanels.find((panel) => panel.dataset.megaProductPanel === panelId);
        this.loadProductsForPanel(activePanel);

        requestAnimationFrame(() => this.updateVisibleCarousel());
      }

      loadProductsForPanel(panel) {
        if (!panel || panel.dataset.productsLoaded === 'true' || panel.dataset.productsLoading === 'true') return;

        const collectionUrl = panel.dataset.collectionUrl;
        const track = panel.querySelector('.mega-menu__products-track');
        const loadingState = panel.querySelector('.mega-menu__products-empty--loading');

        if (!collectionUrl || !track) return;

        panel.dataset.productsLoading = 'true';

        fetch(`${collectionUrl}/products.json?limit=8`)
          .then((response) => response.json())
          .then((data) => {
            const products = Array.isArray(data.products) ? data.products : [];

            if (!products.length) {
              if (loadingState) {
                loadingState.textContent = 'Brak produktow do pokazania w tej kategorii.';
                loadingState.classList.remove('mega-menu__products-empty--loading');
              }
              panel.dataset.productsLoaded = 'true';
              return;
            }

            track.innerHTML = products
              .map((product) => {
                const imageSrc =
                  product?.image?.src || product?.images?.[0]?.src || product?.featured_image || '';
                const productUrl = product?.handle ? `/products/${product.handle}` : '#';
                const price = this.formatMoney(product?.variants?.[0]?.price ?? 0);

                return `
                  <li class="mega-menu__product-card-item">
                    <a href="${productUrl}" class="mega-menu__product-card">
                      <span class="mega-menu__product-media">
                        ${imageSrc ? `<img src="${imageSrc}&width=280" alt="${this.escapeHtml(product.title || '')}" loading="lazy" class="mega-menu__product-image">` : ''}
                      </span>
                      <span class="mega-menu__product-copy">
                        <span class="mega-menu__product-title">${this.escapeHtml(product.title || '')}</span>
                        <span class="mega-menu__product-price">${price}</span>
                      </span>
                    </a>
                  </li>
                `;
              })
              .join('');

            loadingState?.remove();
            panel.dataset.productsLoaded = 'true';
            this.refreshCarousel(panel);
          })
          .catch(() => {
            if (loadingState) {
              loadingState.textContent = 'Nie udalo sie zaladowac produktow.';
              loadingState.classList.remove('mega-menu__products-empty--loading');
            }
          })
          .finally(() => {
            delete panel.dataset.productsLoading;
            requestAnimationFrame(() => this.updateVisibleCarousel());
          });
      }

      refreshCarousel(panel) {
        const existingCarousel = this.carousels.find((carousel) => carousel.panel === panel);
        existingCarousel?.updateButtons();
      }

      formatMoney(cents) {
        const rawValue = `${cents ?? 0}`;
        const normalizedValue = Number(rawValue.replace(',', '.')) || 0;
        const amount = /[.,]/.test(rawValue) ? normalizedValue : normalizedValue / 100;

        return new Intl.NumberFormat('pl-PL', {
          style: 'currency',
          currency: 'PLN',
          minimumFractionDigits: 2,
        }).format(amount);
      }

      escapeHtml(value) {
        return String(value)
          .replaceAll('&', '&amp;')
          .replaceAll('<', '&lt;')
          .replaceAll('>', '&gt;')
          .replaceAll('"', '&quot;')
          .replaceAll("'", '&#39;');
      }

      createCarousel(panel) {
        const scroller = panel.querySelector('[data-mega-product-scroller]');
        const track = panel.querySelector('.mega-menu__products-track');
        const prev = panel.querySelector('[data-mega-carousel-prev]');
        const next = panel.querySelector('[data-mega-carousel-next]');

        if (!scroller || !track || (!prev && !next)) return null;

        const getItems = () => Array.from(track.querySelectorAll('.mega-menu__product-card-item'));
        const getVisibleCount = () => (window.innerWidth <= 1199 ? 2 : 3);

        const updateButtons = () => {
          const hasOverflow = getItems().length > getVisibleCount();
          if (prev) prev.disabled = !hasOverflow;
          if (next) next.disabled = !hasOverflow;
        };

        const rotateItems = (direction) => {
          const items = getItems();
          if (items.length <= getVisibleCount()) return;

          if (direction > 0) {
            track.append(items[0]);
          } else {
            track.prepend(items[items.length - 1]);
          }
        };

        const onPrev = () => rotateItems(-1);
        const onNext = () => rotateItems(1);

        prev?.addEventListener('click', onPrev);
        next?.addEventListener('click', onNext);

        return { panel, scroller, prev, next, onPrev, onNext, onScroll: null, updateButtons };
      }

      updateVisibleCarousel() {
        this.carousels?.forEach((carousel) => {
          if (carousel.panel.classList.contains('is-active')) {
            carousel.updateButtons();
          }
        });
      }
    }
  );
}